import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  StepLog,
  Toggle
} from "../../chunk-GSDDQVLY.js";
import {
  Map as Map2,
  registerOverlay,
  toLngLatArr
} from "../../chunk-HBOVEW7J.js";
import {
  ScrollArea
} from "../../chunk-67S6OTW7.js";
import {
  Badge,
  CopyButton,
  Item,
  ItemActions,
  ItemMedia,
  ItemTitle
} from "../../chunk-SYKHP7A4.js";
import {
  cn
} from "../../chunk-PRYE6Q3K.js";

// src/map/logistics/route-overlay.ts
import * as React3 from "react";
import maplibregl from "maplibre-gl";

// src/map/logistics/color.ts
function oklchToRgb(L, C, H) {
  const hr = H * Math.PI / 180;
  const a = C * Math.cos(hr);
  const b = C * Math.sin(hr);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m2 = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const f = (x) => {
    const v = x <= 31308e-7 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(v * 255)));
  };
  return [
    f(4.0767416621 * l - 3.3077115913 * m2 + 0.2309699292 * s),
    f(-1.2684380046 * l + 2.6097574011 * m2 - 0.3413193965 * s),
    f(-0.0041960863 * l - 0.7034186147 * m2 + 1.707614701 * s)
  ];
}
function toMapColor(value) {
  const m2 = value.match(/oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)/i);
  if (!m2) return value;
  const L = m2[2] === "%" ? parseFloat(m2[1]) / 100 : parseFloat(m2[1]);
  const [r, g, b] = oklchToRgb(L, parseFloat(m2[3]), parseFloat(m2[4]));
  return `rgb(${r}, ${g}, ${b})`;
}

// src/map/logistics/theme.ts
function cssVar(name, fallback) {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? toMapColor(v) : fallback;
}
var CYCLE_FALLBACK = [
  "#3b82f6",
  "#ef4444",
  "#22c55e",
  "#f59e0b",
  "#a855f7",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
  "#f97316",
  "#6366f1"
];
function routePalette() {
  return CYCLE_FALLBACK.map((fb, i) => cssVar(`--color-${String(i + 1).padStart(4, "0")}`, fb));
}
function routeColor(i) {
  const pal = routePalette();
  const n = pal.length;
  return pal[(i % n + n) % n];
}
function statusToken(status) {
  switch ((status || "").toLowerCase()) {
    case "completed":
    case "delivered":
      return cssVar("--success", "#16a34a");
    case "active":
    case "in_transit":
      return cssVar("--info", "#2563eb");
    case "pending":
    case "partially_delivered":
      return cssVar("--warning", "#d97706");
    case "canceled":
    case "cancelled":
    case "failed":
      return cssVar("--destructive", "#dc2626");
    default:
      return cssVar("--muted-foreground", "#64748b");
  }
}

// src/map/logistics/geometry.ts
var FAN_SPACING_M = 40;
var FAN_TAPER_M = 350;
function lngDelta(a, b) {
  return ((b - a + 180) % 360 + 360) % 360 - 180;
}
function segMeters(a, b) {
  const latMid = (a[1] + b[1]) / 2 * (Math.PI / 180);
  const dx = lngDelta(a[0], b[0]) * 111320 * Math.cos(latMid);
  const dy = (b[1] - a[1]) * 111320;
  return Math.hypot(dx, dy);
}
function fanDisplace(coords, offsetM) {
  const n = coords.length;
  if (offsetM === 0 || n < 2) return coords;
  const cum = [0];
  for (let i = 1; i < n; i++) cum[i] = cum[i - 1] + segMeters(coords[i - 1], coords[i]);
  const total = cum[n - 1];
  if (total === 0) return coords;
  const taper = Math.min(FAN_TAPER_M, total / 2);
  const out = [];
  for (let i = 0; i < n; i++) {
    const prev = coords[Math.max(0, i - 1)];
    const next = coords[Math.min(n - 1, i + 1)];
    const latRad = coords[i][1] * (Math.PI / 180);
    const mPerDegLat = 111320;
    const mPerDegLng = 111320 * Math.cos(latRad);
    let ex = lngDelta(prev[0], next[0]) * mPerDegLng;
    let ny = (next[1] - prev[1]) * mPerDegLat;
    const len = Math.hypot(ex, ny) || 1;
    ex /= len;
    ny /= len;
    const nx = -ny;
    const nn = ex;
    const distFromEnd = Math.min(cum[i], total - cum[i]);
    const t = taper > 0 ? Math.min(1, distFromEnd / taper) : 1;
    const off = offsetM * t;
    out.push([coords[i][0] + nx * off / mPerDegLng, coords[i][1] + nn * off / mPerDegLat]);
  }
  return out;
}
function fanOffsets(items, endpoints, idOf) {
  const groups = /* @__PURE__ */ new Map();
  for (const it of items) {
    const { src, dst } = endpoints(it);
    const key = `${src[0].toFixed(5)},${src[1].toFixed(5)}->${dst[0].toFixed(5)},${dst[1].toFixed(5)}`;
    (groups.get(key) ?? groups.set(key, []).get(key)).push(it);
  }
  const offsetById = /* @__PURE__ */ new Map();
  for (const grp of groups.values()) {
    if (grp.length < 2) continue;
    grp.sort((a, b) => idOf(a) < idOf(b) ? -1 : idOf(a) > idOf(b) ? 1 : 0);
    const mid = (grp.length - 1) / 2;
    grp.forEach((it, i) => offsetById.set(idOf(it), (i - mid) * FAN_SPACING_M));
  }
  return offsetById;
}

// src/map/logistics/popups.tsx
import * as React2 from "react";
import { ChevronRight, X } from "lucide-react";

// src/map/logistics/format.ts
function esc(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
var SAFE_HREF = /^(?:https?:|mailto:|tel:|\/\/|\/(?!\/))/i;
function safeHref(url) {
  if (!url) return null;
  const stripped = url.replace(/[\x00-\x20]+/g, "");
  return SAFE_HREF.test(stripped) ? url : null;
}
function fmtDateTime(iso, opts = {}) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return d.toLocaleString(opts.locale, {
    timeZone: opts.timeZone,
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}
function fmtTime(iso, opts = {}) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(opts.locale, { timeZone: opts.timeZone, hour: "2-digit", minute: "2-digit" });
}
function fmtDateTimeNumeric(iso, opts = {}) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const parts = new Intl.DateTimeFormat(opts.locale, {
    timeZone: opts.timeZone,
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
}
var DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
var cap = (s) => s ? s[0].toUpperCase() + s.slice(1) : s;
function fmtHours(hours) {
  if (!hours || !hours.length) return [];
  return [...hours].sort(
    (a, b) => DAY_ORDER.indexOf((a.dayOfWeek || "").toLowerCase()) - DAY_ORDER.indexOf((b.dayOfWeek || "").toLowerCase())
  ).map((h) => {
    const day = cap((h.dayOfWeek || "").slice(0, 3));
    if (h.closed) return `${day}: closed`;
    if (h.timeOpen && h.timeClose) return `${day}: ${h.timeOpen}\u2013${h.timeClose}`;
    return day;
  });
}
function fmtNum(n, decimals) {
  return n.toLocaleString("en-US", decimals != null ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals } : void 0);
}
function fmtKm(m2) {
  return m2 != null ? `${fmtNum(m2 / 1e3, 1)} km` : "\u2014";
}
function fmtMin(s) {
  return s != null ? `${Math.round(s / 60)} min` : "\u2014";
}
function fmtPct(u, decimals = 0) {
  return u != null ? `${(u * 100).toFixed(decimals)}%` : "\u2014";
}
function haversineTotalM(coords) {
  const R = 6371e3;
  let d = 0;
  for (let i = 1; i < coords.length; i++) {
    const [la1, ln1] = coords[i - 1];
    const [la2, ln2] = coords[i];
    const dLa = (la2 - la1) * Math.PI / 180;
    const dLn = (ln2 - ln1) * Math.PI / 180;
    const a = Math.sin(dLa / 2) ** 2 + Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dLn / 2) ** 2;
    d += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return d;
}

// src/map/logistics/summary.ts
function volParts(unit) {
  return {
    scale: unit === "cm3" ? 1e-6 : 1,
    // cm³ auto-converts to m³ for display
    sfx: unit === "pallets" ? "pal." : "m3",
    prec: 1
    // one decimal for every volume figure, pallets or m³
  };
}
function fmtVolume(amount, unit) {
  const { scale, sfx, prec } = volParts(unit);
  return `${fmtNum(amount * scale, prec)} ${sfx}`;
}
function volFraction(used, cap2, unit) {
  if (used == null || cap2 == null) return void 0;
  const { scale, sfx, prec } = volParts(unit);
  return `(${fmtNum(used * scale, prec)} / ${fmtNum(cap2 * scale, prec)} ${sfx})`;
}
function orderAmountLabel(order, unit) {
  const vol = order.amount != null ? fmtVolume(order.amount, unit) : null;
  const wt = order.total_weight != null ? `${fmtNum(order.total_weight, 0)} ${order.weight_unit || "kg"}` : null;
  return [vol, wt].filter(Boolean).join(" / ") || null;
}
function stopShareLabel(stop, route) {
  const volPct = stop.pallet_amount != null && route.pallet_amount ? stop.pallet_amount / route.pallet_amount : null;
  const wtPct = stop.weight != null && route.total_weight ? stop.weight / route.total_weight : null;
  const parts = [];
  if (wtPct != null) parts.push(`${fmtPct(wtPct, 1)} wt`);
  if (volPct != null) parts.push(`${fmtPct(volPct, 1)} vol`);
  return parts.length ? parts.join(" \xB7 ") : null;
}
function routeSummaryRows(route, unit, fmt = {}) {
  const dts = route.drop_datetimes || [];
  const dropCount = route.drop_count != null ? route.drop_count : dts.length;
  const dropStr = `${dropCount} drop${dropCount !== 1 ? "s" : ""}`;
  const roadCoords = route.road_coords?.length ? route.road_coords : route.coords || [];
  const distanceKm = route.road_distance_m != null ? route.road_distance_m / 1e3 : roadCoords.length ? haversineTotalM(roadCoords) / 1e3 : null;
  const rows = [
    { label: "Truck Class", value: route.truck_class || dropStr, detail: route.truck_class ? `(${dropStr})` : void 0 },
    {
      label: unit === "pallets" ? "Pallet Util." : "Vol. Util.",
      value: fmtPct(route.utilization, 1),
      detail: volFraction(route.pallet_amount, route.vol_cap, unit)
    }
  ];
  if (route.weight_util != null || route.total_weight != null || route.weight_cap != null) {
    rows.push({
      label: "Weight Util.",
      value: fmtPct(route.weight_util, 1),
      detail: route.total_weight != null && route.weight_cap != null ? `(${fmtNum(route.total_weight, 1)} / ${fmtNum(route.weight_cap, 1)} tons)` : void 0
    });
  }
  if (route.pickup_datetime) rows.push({ label: "Pickup Time", value: fmtDateTimeNumeric(route.pickup_datetime, fmt) });
  if (dts.length > 0) rows.push({ label: "1st Drop Time", value: fmtDateTimeNumeric(dts[0], fmt) });
  if (dts.length > 1) rows.push({ label: "Last Drop Time", value: fmtDateTimeNumeric(dts[dts.length - 1], fmt) });
  rows.push({ label: "Route Distance", value: distanceKm != null ? `${fmtNum(distanceKm, 1)} km` : "\u2014" });
  if (route.furthest_distance_m != null) rows.push({ label: "Furthest Drop", value: fmtKm(route.furthest_distance_m) });
  return rows;
}
function orderSummaryRows(order, unit, fmt = {}) {
  const rows = [];
  if (order.customer_req_datetime) rows.push({ label: "Requested", value: fmtDateTimeNumeric(order.customer_req_datetime, fmt) });
  if (order.appointment_datetime) rows.push({ label: "Appointment", value: fmtDateTimeNumeric(order.appointment_datetime, fmt) });
  if (order.delivery_deadline) rows.push({ label: "Deadline", value: fmtDateTimeNumeric(order.delivery_deadline, fmt) });
  rows.push({ label: "Amount", value: orderAmountLabel(order, unit) ?? "\u2014" });
  if (order.labels?.length) rows.push({ label: "Labels", value: order.labels.join(", "), chips: order.labels });
  return rows;
}

// src/map/logistics/metric-rows.tsx
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
function LabelPills({ labels, className }) {
  return /* @__PURE__ */ jsx("span", { className: cn("flex flex-wrap gap-1", className), children: labels.map((l) => /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "px-1.5 py-0 font-mono text-[10px] font-normal", children: l }, l)) });
}
function MetricRows({ rows, className }) {
  if (!rows.length) return null;
  return /* @__PURE__ */ jsx("div", { className: cn("grid grid-cols-[max-content_1fr] items-baseline gap-x-3 gap-y-0.5 text-sm", className), children: rows.map((r) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: r.label }),
    r.chips?.length ? /* @__PURE__ */ jsx(LabelPills, { labels: r.chips, className: "self-center" }) : (
      // No truncate — the fraction detail must stay legible; wrap within the cell instead.
      /* @__PURE__ */ jsxs("span", { className: "min-w-0 font-mono tabular-nums break-words text-foreground", children: [
        r.value,
        r.detail ? /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
          " ",
          r.detail
        ] }) : null
      ] })
    )
  ] }, r.label)) });
}

// src/map/logistics/popups.tsx
import { Fragment as Fragment3, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var DEFAULT_POPUP_MAX_HEIGHT = "min(60vh, 20rem)";
function StatusBadge({ status }) {
  if (!status) return null;
  const c = statusToken(status);
  return /* @__PURE__ */ jsx2(Badge, { variant: "outline", className: "shrink-0 font-mono text-2xs uppercase", style: { color: c, borderColor: c }, children: status });
}
function GridRow({ label, value, mono = true }) {
  return /* @__PURE__ */ jsxs2(Fragment3, { children: [
    /* @__PURE__ */ jsx2("span", { className: "text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx2("span", { className: cn("min-w-0 break-words text-foreground", mono && "font-mono tabular-nums"), children: value })
  ] });
}
function HoverGrid({ children }) {
  return /* @__PURE__ */ jsx2("div", { className: "grid grid-cols-[max-content_1fr] items-baseline gap-x-3 gap-y-0.5", children });
}
function HoverHead({ color, ref_, status }) {
  return /* @__PURE__ */ jsxs2("div", { className: "mb-1 flex items-center gap-2", children: [
    /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "size-2 shrink-0 rounded-full", style: { background: color } }),
    /* @__PURE__ */ jsx2("span", { className: "min-w-0 flex-1 truncate font-mono font-bold", style: { color }, children: ref_ || "\u2014" }),
    /* @__PURE__ */ jsx2(StatusBadge, { status })
  ] });
}
function MicroLabel({ children, className }) {
  return /* @__PURE__ */ jsx2("div", { className: cn("font-mono text-2xs uppercase tracking-[0.08em] text-muted-foreground", className), children });
}
function DetailCard({ children }) {
  return /* @__PURE__ */ jsx2("div", { className: "my-1 rounded-sm bg-foreground/5 px-3 py-2", children });
}
function placeName(name, code) {
  if (!name) return "\u2014";
  return /* @__PURE__ */ jsxs2(Fragment3, { children: [
    name,
    code ? /* @__PURE__ */ jsxs2("span", { className: "font-mono font-normal text-muted-foreground", children: [
      " (",
      code,
      ")"
    ] }) : null
  ] });
}
function NameLine({ name, code }) {
  if (!name) return null;
  return /* @__PURE__ */ jsx2("div", { className: "font-medium text-foreground", children: placeName(name, code) });
}
function HoursLines({ hours }) {
  const lines = fmtHours(hours);
  if (!lines.length) return null;
  return /* @__PURE__ */ jsx2("div", { className: "leading-tight text-muted-foreground", children: lines.map((h, i) => /* @__PURE__ */ jsxs2(React2.Fragment, { children: [
    i > 0 ? /* @__PURE__ */ jsx2("br", {}) : null,
    h
  ] }, i)) });
}
function TruckClassRow({ tc, capacityUnit }) {
  const name = [tc.code, tc.name && tc.name !== tc.code ? tc.name : null].filter(Boolean).join(" \xB7 ");
  const volCap = capacityUnit === "pallets" ? tc.maxPallets : tc.volumeCapacity;
  const caps = [
    volCap != null ? fmtVolume(volCap, capacityUnit) : null,
    tc.weightCapacity != null ? `${fmtNum(tc.weightCapacity, 0)} kg` : null
  ].filter(Boolean);
  return /* @__PURE__ */ jsxs2("div", { className: "flex flex-col", children: [
    /* @__PURE__ */ jsx2("span", { className: "text-foreground", children: name || "\u2014" }),
    caps.length ? /* @__PURE__ */ jsx2("span", { className: "font-mono text-2xs text-muted-foreground", children: caps.join(" \xB7 ") }) : null
  ] });
}
function DetailHeader({
  accent,
  refText,
  href,
  status,
  onClose
}) {
  const safe = href ? safeHref(href) : null;
  const ident = safe ? /* @__PURE__ */ jsx2(
    "a",
    {
      href: safe,
      target: "_blank",
      rel: "noopener",
      className: "min-w-0 flex-1 truncate font-mono font-bold no-underline hover:underline",
      style: { color: accent },
      children: refText || "\u2014"
    }
  ) : /* @__PURE__ */ jsx2("span", { className: "min-w-0 flex-1 truncate font-mono font-bold", style: { color: accent }, children: refText || "\u2014" });
  return /* @__PURE__ */ jsxs2("div", { className: "relative flex items-center gap-1.5 border-b border-border bg-popover py-2.5 pr-3 pl-4", children: [
    /* @__PURE__ */ jsx2("span", { "aria-hidden": "true", className: "absolute inset-y-1.5 left-1.5 w-1 rounded-full", style: { backgroundColor: accent } }),
    ident,
    refText ? /* @__PURE__ */ jsx2(CopyButton, { value: refText, className: "-my-1 text-muted-foreground" }) : null,
    /* @__PURE__ */ jsx2(StatusBadge, { status }),
    onClose ? /* @__PURE__ */ jsx2(
      "button",
      {
        type: "button",
        onClick: onClose,
        "aria-label": "Unpin",
        className: "-my-1 -mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_svg]:size-3",
        children: /* @__PURE__ */ jsx2(X, { "aria-hidden": "true" })
      }
    ) : null
  ] });
}
function RoutePopup({
  route,
  color,
  variant,
  capacityUnit,
  format = {},
  maxHeight = DEFAULT_POPUP_MAX_HEIGHT,
  stopsOpen,
  onStopsOpenChange,
  onClose
}) {
  if (variant === "hover") {
    const ref2 = route.ship_ref || route.label || route.path_id || "";
    const util = `${fmtPct(route.utilization, 1)} ${capacityUnit === "pallets" ? "pal." : "m3"}` + (route.weight_util != null ? ` / ${fmtPct(route.weight_util, 1)} wght.` : "");
    return /* @__PURE__ */ jsxs2("div", { "data-pn-popup": "route", "data-variant": "hover", className: "min-w-40 px-3 py-2 text-xs", children: [
      /* @__PURE__ */ jsx2(HoverHead, { color, ref_: ref2, status: route.status }),
      /* @__PURE__ */ jsxs2(HoverGrid, { children: [
        route.truck_class ? /* @__PURE__ */ jsx2(GridRow, { label: "Class", value: route.truck_class }) : null,
        /* @__PURE__ */ jsx2(GridRow, { label: "Util", value: util }),
        /* @__PURE__ */ jsx2(GridRow, { label: "Distance", value: fmtKm(route.road_distance_m) }),
        /* @__PURE__ */ jsx2(GridRow, { label: "Pickup", value: route.pickup_datetime ? fmtDateTimeNumeric(route.pickup_datetime, format) : "\u2014" })
      ] })
    ] });
  }
  const ref = route.ship_ref || route.label || "";
  const stops = route.stops || [];
  const dts = route.drop_datetimes || [];
  const allRefs = stops.slice(1).flatMap((s) => s.order_refs || []).join("\n");
  const steps = stops.map((s, i) => {
    const isPickup = i === 0;
    const arriveDt = isPickup ? route.pickup_datetime || null : dts[i - 1] || null;
    return {
      label: placeName(s.name, s.code && s.code !== s.name ? s.code : null),
      detail: fmtTime(arriveDt, format) || void 0,
      meta: isPickup ? "PICKUP" : stopShareLabel(s, route) ?? void 0,
      status: isPickup ? "active" : void 0
    };
  });
  return /* @__PURE__ */ jsxs2("div", { "data-pn-popup": "route", "data-variant": "detail", className: "w-66 text-xs", children: [
    /* @__PURE__ */ jsx2(DetailHeader, { accent: color, refText: ref, href: route.ship_url, status: route.status, onClose }),
    /* @__PURE__ */ jsx2(MetricRows, { rows: routeSummaryRows(route, capacityUnit, format), className: "px-3 py-2 text-2xs" }),
    /* @__PURE__ */ jsxs2(
      Collapsible,
      {
        ...stopsOpen != null ? { open: stopsOpen, onOpenChange: onStopsOpenChange } : { defaultOpen: true },
        className: "group/stops border-t border-border/60 pb-1",
        children: [
          /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-1 pr-1", children: [
            /* @__PURE__ */ jsxs2(CollapsibleTrigger, { className: "flex flex-1 items-center gap-1 px-3 py-2 font-mono text-2xs tracking-[0.08em] text-muted-foreground uppercase outline-none hover:text-foreground focus-visible:text-foreground", children: [
              /* @__PURE__ */ jsx2(ChevronRight, { "aria-hidden": "true", className: "size-3 transition-transform group-data-[state=open]/stops:rotate-90" }),
              "Stops (",
              steps.length,
              ")"
            ] }),
            allRefs ? /* @__PURE__ */ jsx2(CopyButton, { value: allRefs, className: "text-muted-foreground" }) : null
          ] }),
          /* @__PURE__ */ jsx2(CollapsibleContent, { className: "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down", children: /* @__PURE__ */ jsx2(ScrollArea, { className: "w-full", style: { maxHeight }, children: /* @__PURE__ */ jsx2(StepLog, { steps, className: "px-3 pt-1 pb-2 text-2xs", detailClassName: "font-mono" }) }) })
        ]
      }
    )
  ] });
}
function OrderPopup({ order, color, variant, capacityUnit, format = {}, maxHeight = DEFAULT_POPUP_MAX_HEIGHT }) {
  if (variant === "hover") {
    const ref2 = order.order_ref || order.order_id || "";
    const loc2 = [order.location.name || null, order.location.code ? `(${order.location.code})` : null].filter(Boolean).join(" ");
    const amount = orderAmountLabel(order, capacityUnit);
    return /* @__PURE__ */ jsxs2("div", { "data-pn-popup": "order", "data-variant": "hover", className: "min-w-40 px-3 py-2 text-xs", children: [
      /* @__PURE__ */ jsx2(HoverHead, { color, ref_: ref2, status: order.status }),
      /* @__PURE__ */ jsxs2(HoverGrid, { children: [
        loc2 ? /* @__PURE__ */ jsx2(GridRow, { label: "Location", value: loc2, mono: false }) : null,
        order.customer_req_datetime ? /* @__PURE__ */ jsx2(GridRow, { label: "Requested", value: fmtDateTimeNumeric(order.customer_req_datetime, format) }) : null,
        order.delivery_deadline ? /* @__PURE__ */ jsx2(GridRow, { label: "Deadline", value: fmtDateTimeNumeric(order.delivery_deadline, format) }) : null,
        amount ? /* @__PURE__ */ jsx2(GridRow, { label: "Amount", value: amount }) : null,
        order.labels?.length ? /* @__PURE__ */ jsxs2(Fragment3, { children: [
          /* @__PURE__ */ jsx2("span", { className: "text-muted-foreground", children: "Labels" }),
          /* @__PURE__ */ jsx2(LabelPills, { labels: order.labels, className: "self-center" })
        ] }) : null
      ] })
    ] });
  }
  const ref = order.order_ref || order.order_id || "";
  const loc = order.location;
  const tcs = loc.eligible_truck_classes || [];
  return /* @__PURE__ */ jsxs2("div", { "data-pn-popup": "order", "data-variant": "detail", className: "w-66 text-xs", children: [
    /* @__PURE__ */ jsx2(DetailHeader, { accent: color, refText: ref, href: order.order_url, status: order.status }),
    /* @__PURE__ */ jsx2(MetricRows, { rows: orderSummaryRows(order, capacityUnit, format), className: "px-3 py-2 text-2xs" }),
    /* @__PURE__ */ jsxs2(Collapsible, { defaultOpen: true, className: "group/loc border-t border-border/60 pb-1", children: [
      /* @__PURE__ */ jsxs2(CollapsibleTrigger, { className: "flex w-full items-center gap-1 px-3 py-2 font-mono text-2xs tracking-[0.08em] text-muted-foreground uppercase outline-none hover:text-foreground focus-visible:text-foreground", children: [
        /* @__PURE__ */ jsx2(ChevronRight, { "aria-hidden": "true", className: "size-3 transition-transform group-data-[state=open]/loc:rotate-90" }),
        "Delivery location"
      ] }),
      /* @__PURE__ */ jsx2(CollapsibleContent, { className: "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down", children: /* @__PURE__ */ jsx2(ScrollArea, { className: "w-full", style: { maxHeight }, children: /* @__PURE__ */ jsx2("div", { className: "px-2 pb-2 text-2xs", children: /* @__PURE__ */ jsxs2(DetailCard, { children: [
        /* @__PURE__ */ jsx2(NameLine, { name: loc.name, code: loc.code }),
        loc.requires_appointment != null ? /* @__PURE__ */ jsx2("div", { className: "text-muted-foreground", children: loc.requires_appointment ? "Appointment required" : "No appointment required" }) : null,
        fmtHours(loc.hours).length ? /* @__PURE__ */ jsxs2(Fragment3, { children: [
          /* @__PURE__ */ jsx2(MicroLabel, { className: "mt-1", children: "Operating hours" }),
          /* @__PURE__ */ jsx2(HoursLines, { hours: loc.hours })
        ] }) : null,
        tcs.length ? /* @__PURE__ */ jsxs2(Fragment3, { children: [
          /* @__PURE__ */ jsx2(MicroLabel, { className: "mt-1", children: "Eligible truck classes" }),
          /* @__PURE__ */ jsx2("div", { className: "mt-0.5 flex flex-col gap-0.5", children: tcs.map((tc, i) => /* @__PURE__ */ jsx2(TruckClassRow, { tc, capacityUnit }, i)) })
        ] }) : null
      ] }) }) }) })
    ] })
  ] });
}

// src/map/logistics/popup-mount.ts
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
var PopupMount = class {
  container;
  root;
  constructor() {
    this.container = document.createElement("div");
    this.root = createRoot(this.container);
  }
  /** Render `node` into the reused root and return the container to hand to `popup.setDOMContent`. */
  render(node) {
    flushSync(() => this.root.render(node));
    return this.container;
  }
  /** Unmount the root. Deferred to a microtask — unmounting synchronously from inside a React
   *  commit or event handler triggers a warning. */
  destroy() {
    const root = this.root;
    queueMicrotask(() => root.unmount());
  }
};

// src/map/logistics/route-overlay.ts
var SRC = "pn-routes-paths";
var L_SOLID = "pn-routes-lines-solid";
var L_DASH = "pn-routes-lines-dashed";
var L_TOP = "pn-routes-lines-top";
var SRC_M = "pn-routes-markers";
var L_HIT = "pn-routes-marker-hits";
var L_DOT = "pn-routes-marker-dots";
var L_LABEL = "pn-routes-marker-labels";
var DIM = 0.12;
function emptyFC() {
  return { type: "FeatureCollection", features: [] };
}
function cssVar2(name, fallback) {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? toMapColor(v) : fallback;
}
var IS_PICKUP = ["==", ["get", "kind"], "pickup"];
var RouteOverlay = class {
  map;
  countEl;
  hoverPopup;
  routesData = [];
  routeVisible = [];
  indexByPath = /* @__PURE__ */ new Map();
  markerMids = [];
  emph = /* @__PURE__ */ new Map();
  hoveredPathId = null;
  pinnedPathIds = /* @__PURE__ */ new Set();
  hoverPopupPid = null;
  focusRestoreTimer = null;
  loaded = false;
  pendingRoutes = null;
  dirty = false;
  unbinders = [];
  onLoad;
  popupMount = new PopupMount();
  capacityUnit;
  onRender = null;
  onHover = null;
  /** Fires whenever the pinned set changes (pin/unpin, or a data update that drops stale pins),
   *  with the pinned path_ids in pin order. The single source of truth for pinned state. */
  onPinnedChange = null;
  emitPinned() {
    this.onPinnedChange?.([...this.pinnedPathIds]);
  }
  constructor(map, opts) {
    this.map = map;
    this.countEl = opts.countEl ?? null;
    this.capacityUnit = opts.capacityUnit;
    this.hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 14,
      maxWidth: "340px",
      // pn-map-pop--hover sets pointer-events:none so the tooltip never blocks
      // map drag/zoom under the cursor.
      className: "pn-map-pop pn-map-pop--hover"
    });
    this.unbinders.push(registerOverlay(map, this));
    if (map.loaded()) this.init();
    else {
      this.onLoad = () => this.init();
      map.once("load", this.onLoad);
    }
  }
  init() {
    this.addLayers();
    this.wireHover();
    const onStyle = () => this.reinstall();
    this.map.on("styledata", onStyle);
    this.unbinders.push(() => this.map.off("styledata", onStyle));
    this.loaded = true;
    if (this.pendingRoutes) {
      const r = this.pendingRoutes;
      this.pendingRoutes = null;
      this.routesData = r;
      this.routeVisible = r.map(() => true);
      this.applyData(true);
    }
  }
  reinstall() {
    if (!this.loaded || this.map.getSource(SRC)) return;
    this.addLayers();
    if (this.routesData.length) this.applyData(false);
  }
  addLayers() {
    const bg = cssVar2("--background", "#ffffff");
    this.map.addSource(SRC, { type: "geojson", data: emptyFC(), promoteId: "pathId" });
    const linePaint = {
      "line-color": ["get", "color"],
      "line-width": [
        "case",
        ["boolean", ["feature-state", "hovered"], false],
        8,
        ["get", "roaded"],
        6,
        4
      ],
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "hovered"], false],
        1,
        ["boolean", ["feature-state", "dimmed"], false],
        0.1,
        0.85
      ]
    };
    const lineLayout = { "line-cap": "round", "line-join": "round" };
    this.map.addLayer({ id: L_SOLID, type: "line", source: SRC, filter: ["!", ["get", "partial"]], paint: linePaint, layout: lineLayout });
    this.map.setPaintProperty(L_SOLID, "line-opacity-transition", { duration: 100, delay: 0 });
    this.map.addLayer({
      id: L_DASH,
      type: "line",
      source: SRC,
      filter: ["get", "partial"],
      paint: { ...linePaint, "line-dasharray": [2, 1.5] },
      layout: lineLayout
    });
    this.map.setPaintProperty(L_DASH, "line-opacity-transition", { duration: 100, delay: 0 });
    this.map.addLayer({
      id: L_TOP,
      type: "line",
      source: SRC,
      filter: ["in", ["get", "pathId"], ["literal", []]],
      paint: linePaint,
      layout: lineLayout
    });
    this.map.setPaintProperty(L_TOP, "line-opacity-transition", { duration: 100, delay: 0 });
    this.map.addSource(SRC_M, { type: "geojson", data: emptyFC(), promoteId: "mid" });
    this.map.addLayer({
      id: L_HIT,
      type: "circle",
      source: SRC_M,
      paint: {
        "circle-radius": ["case", ["==", ["get", "kind"], "pickup"], 12, 10],
        "circle-opacity": 0,
        "circle-stroke-width": 0
      }
    });
    const emph = ["boolean", ["feature-state", "hovered"], false];
    this.map.addLayer({
      id: L_DOT,
      type: "circle",
      source: SRC_M,
      layout: { "circle-sort-key": ["case", IS_PICKUP, 1, 0] },
      paint: {
        "circle-radius": ["case", emph, 9, 7],
        "circle-color": ["case", IS_PICKUP, ["get", "color"], bg],
        "circle-stroke-color": ["case", IS_PICKUP, bg, ["get", "color"]],
        "circle-stroke-width": 2.5,
        "circle-pitch-alignment": "map",
        "circle-opacity": ["case", ["boolean", ["feature-state", "dimmed"], false], DIM, 1],
        "circle-stroke-opacity": ["case", ["boolean", ["feature-state", "dimmed"], false], DIM, 1]
      }
    });
    this.map.setPaintProperty(L_DOT, "circle-opacity-transition", { duration: 100, delay: 0 });
    this.map.setPaintProperty(L_DOT, "circle-stroke-opacity-transition", { duration: 100, delay: 0 });
    this.map.addLayer({
      id: L_LABEL,
      type: "symbol",
      source: SRC_M,
      layout: {
        "text-field": ["get", "label"],
        "text-font": ["JetBrains Mono Bold", "Noto Sans Bold"],
        "text-size": 10,
        "text-allow-overlap": false,
        "text-ignore-placement": false,
        "text-padding": 3
      },
      paint: {
        "text-color": ["get", "color"],
        "text-opacity": ["case", ["boolean", ["feature-state", "dimmed"], false], DIM, 1]
      }
    });
    this.map.setPaintProperty(L_LABEL, "text-opacity-transition", { duration: 100, delay: 0 });
  }
  /** Re-resolve the bg-derived dot fill/ring after a theme swap (route colours refresh via data). */
  applyMarkerBg() {
    if (!this.map.getLayer(L_DOT)) return;
    const bg = cssVar2("--background", "#ffffff");
    this.map.setPaintProperty(L_DOT, "circle-color", ["case", IS_PICKUP, ["get", "color"], bg]);
    this.map.setPaintProperty(L_DOT, "circle-stroke-color", ["case", IS_PICKUP, bg, ["get", "color"]]);
  }
  // ── Public API ────────────────────────────────────────────────────────────
  setRoutes(routes) {
    this.clearTransient();
    if (!this.loaded) {
      this.pendingRoutes = Array.isArray(routes) ? routes.slice() : [];
      this.routesData = this.pendingRoutes;
      this.routeVisible = this.routesData.map(() => true);
      this.reconcilePins();
      this.onRender?.(this.routesData);
      return;
    }
    this.routesData = Array.isArray(routes) ? routes.slice() : [];
    this.routeVisible = this.routesData.map(() => true);
    this.applyData(true);
    this.reconcilePins();
    this.onRender?.(this.routesData);
  }
  /** Drop pins whose route is no longer present after a data update — keeps the deck from holding
   *  cards for routes that vanished. Emits only if something changed. */
  reconcilePins() {
    if (this.pinnedPathIds.size === 0) return;
    const valid = new Set(this.routesData.map((r) => r.path_id));
    let changed = false;
    for (const id of this.pinnedPathIds) {
      if (!valid.has(id)) {
        this.pinnedPathIds.delete(id);
        changed = true;
      }
    }
    if (changed) {
      this.applyEmphasis();
      this.emitPinned();
    }
  }
  clear() {
    this.clearTransient();
    this.routesData = [];
    this.routeVisible = [];
    this.pendingRoutes = null;
    this.indexByPath.clear();
    this.markerMids = [];
    this.emph.clear();
    const hadPins = this.pinnedPathIds.size > 0;
    this.pinnedPathIds.clear();
    if (this.loaded) {
      this.getSource(SRC)?.setData(emptyFC());
      this.getSource(SRC_M)?.setData(emptyFC());
    }
    if (this.countEl) this.countEl.textContent = "0";
    if (hadPins) this.emitPinned();
    this.onRender?.(this.routesData);
  }
  destroy() {
    if (this.onLoad) {
      this.map.off("load", this.onLoad);
      this.onLoad = void 0;
    }
    for (const off of this.unbinders) off();
    this.unbinders = [];
    this.popupMount.destroy();
    this.clearTransient();
    for (const id of [L_LABEL, L_DOT, L_HIT, L_TOP, L_DASH, L_SOLID]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    for (const id of [SRC_M, SRC]) if (this.map.getSource(id)) this.map.removeSource(id);
    this.loaded = false;
  }
  /** Categorical color for route `i` (palette-derived, honouring color_idx). */
  routeColor(i) {
    const pal = routePalette();
    const r = this.routesData[i];
    const idx = r && r.color_idx != null ? r.color_idx : i;
    return pal[(idx % pal.length + pal.length) % pal.length] ?? pal[0];
  }
  /** Re-render lines + markers with the current palette + theme (no re-fit). */
  refreshColors() {
    if (!this.loaded) return;
    this.applyData(false);
    this.applyMarkerBg();
  }
  isVisible(i) {
    return !!this.routeVisible[i];
  }
  isHovered(i) {
    const r = this.routesData[i];
    return !!r && r.path_id === this.hoveredPathId;
  }
  setVisible(i, visible) {
    if (i < 0 || i >= this.routeVisible.length) return;
    if (this.routeVisible[i] === visible) return;
    this.routeVisible[i] = visible;
    this.scheduleRebuild();
  }
  /** Zoom to a single route and briefly flash it. */
  focusRoute(i) {
    const r = this.routesData[i];
    if (!r) return;
    const b = this.routeBounds(r);
    if (b) this.map.fitBounds(b, { padding: 80, maxZoom: 15, duration: 500 });
    const pid = r.path_id;
    this.setHovered(pid);
    if (this.focusRestoreTimer) clearTimeout(this.focusRestoreTimer);
    this.focusRestoreTimer = setTimeout(() => {
      if (this.hoveredPathId === pid) this.setHovered(null);
      this.focusRestoreTimer = null;
    }, 1800);
  }
  hoverRoute(i) {
    this.setHovered(this.routesData[i]?.path_id ?? null);
  }
  clearHover() {
    this.setHovered(null);
  }
  isPinned(i) {
    const r = this.routesData[i];
    return !!r && this.pinnedPathIds.has(r.path_id);
  }
  pin(i) {
    const r = this.routesData[i];
    if (!r || this.pinnedPathIds.has(r.path_id)) return;
    this.pinnedPathIds.add(r.path_id);
    this.applyEmphasis();
    this.emitPinned();
  }
  unpin(i) {
    const r = this.routesData[i];
    if (!r || !this.pinnedPathIds.has(r.path_id)) return;
    this.pinnedPathIds.delete(r.path_id);
    if (this.hoveredPathId === r.path_id) this.setHovered(null);
    this.applyEmphasis();
    this.emitPinned();
  }
  togglePin(i) {
    if (this.isPinned(i)) this.unpin(i);
    else this.pin(i);
  }
  toggleAll() {
    if (!this.routeVisible.length) return;
    const target = this.routeVisible.some((v) => !v);
    let changed = false;
    this.routeVisible = this.routeVisible.map((v) => {
      if (v !== target) changed = true;
      return target;
    });
    if (changed) this.scheduleRebuild();
  }
  /** Extent of the currently-visible, non-dimmed routes, or null when none qualify. While a
   *  hover/pin spotlight is active, dimmed routes are faded to near-invisible (DIM) — excluding
   *  them means a reframe fits the emphasised (pinned + hovered) set, not routes that are
   *  technically drawn but not actually on screen. With nothing pinned/hovered, nothing is
   *  dimmed, so this is exactly "every visible route" as before. */
  getBounds() {
    const b = new maplibregl.LngLatBounds();
    let any = false;
    this.routesData.forEach((r, i) => {
      if (!this.routeVisible[i] || this.isDimmed(r.path_id)) return;
      for (const ll of this.routeLngLats(r)) {
        b.extend(ll);
        any = true;
      }
    });
    return any ? b : null;
  }
  // ── Rendering ───────────────────────────────────────────────────────────────
  getSource(id) {
    return this.map.getSource(id);
  }
  scheduleRebuild() {
    if (this.dirty || !this.loaded) return;
    this.dirty = true;
    queueMicrotask(() => {
      this.dirty = false;
      this.applyData(false);
    });
  }
  applyData(fit) {
    const lineSrc = this.getSource(SRC);
    const markerSrc = this.getSource(SRC_M);
    if (!lineSrc || !markerSrc) return;
    const drawable = this.routesData.map((r, i) => ({ r, i })).filter(({ i }) => this.routeVisible[i]);
    this.indexByPath.clear();
    for (const { r, i } of drawable) this.indexByPath.set(r.path_id, i);
    const llCache = /* @__PURE__ */ new Map();
    const llOf = (r) => {
      let ll = llCache.get(r.path_id);
      if (!ll) {
        ll = this.routeLngLats(r);
        llCache.set(r.path_id, ll);
      }
      return ll;
    };
    const offsets = fanOffsets(
      drawable.map(({ r }) => r),
      (r) => {
        const ll = llOf(r);
        return { src: ll[0] ?? [0, 0], dst: ll[ll.length - 1] ?? [0, 0] };
      },
      (r) => r.path_id
    );
    const lineFeatures = [];
    const markerFeatures = [];
    this.markerMids = [];
    for (const { r, i } of drawable) {
      const color = this.routeColor(i);
      const ll = llOf(r);
      if (ll.length >= 2) {
        const coords = fanDisplace(ll, offsets.get(r.path_id) ?? 0);
        const roaded = !!(r.road_coords && r.road_coords.length);
        lineFeatures.push({
          type: "Feature",
          geometry: { type: "LineString", coordinates: coords },
          properties: { pathId: r.path_id, color, roaded, partial: roaded && !!r.road_partial }
        });
      }
      (r.stops || []).forEach((s, idx) => {
        if (s.lat == null || s.lng == null) return;
        const mid = `${r.path_id}:${idx}`;
        this.markerMids.push({ mid, pathId: r.path_id });
        markerFeatures.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: [s.lng, s.lat] },
          properties: { mid, pathId: r.path_id, kind: idx === 0 ? "pickup" : "drop", color, label: idx === 0 ? "" : String(idx) }
        });
      });
    }
    lineSrc.setData({ type: "FeatureCollection", features: lineFeatures });
    markerSrc.setData({ type: "FeatureCollection", features: markerFeatures });
    this.emph.clear();
    this.applyEmphasis();
    if (this.countEl) this.countEl.textContent = String(drawable.length);
    if (fit) {
      const b = new maplibregl.LngLatBounds();
      let any = false;
      for (const { r } of drawable) for (const c of llOf(r)) {
        b.extend(c);
        any = true;
      }
      if (any) this.map.fitBounds(b, { padding: 60, duration: 0 });
    }
  }
  // ── Hover / pin ────────────────────────────────────────────────────────────
  wireHover() {
    const enter = (pid, at) => {
      this.setHovered(pid);
      this.map.getCanvas().style.cursor = "pointer";
      const i = this.indexByPath.get(pid);
      if (i != null) {
        this.hoverPopup.setLngLat(at);
        if (pid !== this.hoverPopupPid) {
          this.hoverPopup.setDOMContent(
            this.popupMount.render(
              React3.createElement(RoutePopup, {
                route: this.routesData[i],
                color: this.routeColor(i),
                variant: "hover",
                capacityUnit: this.capacityUnit
              })
            )
          );
          this.hoverPopupPid = pid;
        }
        this.hoverPopup.addTo(this.map);
      }
    };
    const leave = () => {
      this.map.getCanvas().style.cursor = "";
      this.setHovered(null);
      this.hoverPopup.remove();
      this.hoverPopupPid = null;
    };
    for (const layer of [L_SOLID, L_DASH]) {
      const onMove = (e) => {
        const f = e.features?.[0];
        if (f?.id != null) enter(String(f.id), [e.lngLat.lng, e.lngLat.lat]);
      };
      this.map.on("mousemove", layer, onMove);
      this.map.on("mouseleave", layer, leave);
      this.unbinders.push(
        () => this.map.off("mousemove", layer, onMove),
        () => this.map.off("mouseleave", layer, leave)
      );
    }
    const onHitMove = (e) => {
      const f = e.features?.[0];
      const pid = f?.properties?.pathId != null ? String(f.properties.pathId) : null;
      if (pid) enter(pid, f.geometry.coordinates);
    };
    this.map.on("mousemove", L_HIT, onHitMove);
    this.map.on("mouseleave", L_HIT, leave);
    this.unbinders.push(
      () => this.map.off("mousemove", L_HIT, onHitMove),
      () => this.map.off("mouseleave", L_HIT, leave)
    );
  }
  setHovered(pid) {
    if (this.hoveredPathId === pid) return;
    this.hoveredPathId = pid;
    this.applyEmphasis();
    this.onHover?.(pid != null ? this.indexByPath.get(pid) ?? null : null);
  }
  // A pinned route gets the exact same visual treatment as a hovered one (thicker line, bigger
  // dot, drawn on L_TOP) — it stays emphasised independent of whatever else is hovered. Shared
  // with getBounds() so a reframe fits exactly what's emphasised, not what's dimmed out.
  isEmphasized(id) {
    return id === this.hoveredPathId || this.pinnedPathIds.has(id);
  }
  isDimmed(id) {
    return (this.hoveredPathId !== null || this.pinnedPathIds.size > 0) && !this.isEmphasized(id);
  }
  applyEmphasis() {
    for (const r of this.routesData) {
      const id = r.path_id;
      this.safeSetState(SRC, id, { hovered: this.isEmphasized(id), dimmed: this.isDimmed(id) });
    }
    for (const { mid, pathId } of this.markerMids) {
      this.safeSetState(SRC_M, mid, { hovered: this.isEmphasized(pathId), dimmed: this.isDimmed(pathId) });
    }
    const top = new Set(this.pinnedPathIds);
    if (this.hoveredPathId) top.add(this.hoveredPathId);
    const filter = ["in", ["get", "pathId"], ["literal", [...top]]];
    if (this.map.getLayer(L_TOP)) this.map.setFilter(L_TOP, filter);
  }
  safeSetState(source, id, state) {
    const key = `${source}\0${id}`;
    const sig = `${state.hovered ? 1 : 0}${state.dimmed ? 1 : 0}`;
    if (this.emph.get(key) === sig) return;
    if (!this.map.getSource(source)) return;
    try {
      this.map.setFeatureState({ source, id }, state);
      this.emph.set(key, sig);
    } catch {
    }
  }
  clearTransient() {
    if (this.focusRestoreTimer) {
      clearTimeout(this.focusRestoreTimer);
      this.focusRestoreTimer = null;
    }
    this.hoveredPathId = null;
    this.hoverPopupPid = null;
    this.hoverPopup.remove();
  }
  // ── Geometry helpers ──────────────────────────────────────────────────────
  routeLngLats(r) {
    const raw = r.road_coords?.length ? r.road_coords : r.coords || [];
    return toLngLatArr(raw);
  }
  routeBounds(r) {
    const b = new maplibregl.LngLatBounds();
    let any = false;
    for (const ll of this.routeLngLats(r)) {
      b.extend(ll);
      any = true;
    }
    for (const s of r.stops || []) {
      if (s.lat != null && s.lng != null) {
        b.extend([s.lng, s.lat]);
        any = true;
      }
    }
    return any ? b : null;
  }
};

// src/map/logistics/order-overlay.ts
import * as React4 from "react";
import maplibregl2 from "maplibre-gl";
var SRC2 = "pn-orders-points";
var L_POINT = "pn-orders-unclustered";
var L_CLUSTER = "pn-orders-clusters";
var L_CLUSTER_COUNT = "pn-orders-cluster-count";
function emptyFC2() {
  return { type: "FeatureCollection", features: [] };
}
function cssVar3(name, fallback) {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? toMapColor(v) : fallback;
}
var OrderOverlay = class {
  map;
  popup;
  ordersData = [];
  orderVisible = [];
  oidByIndex = [];
  indexByOid = /* @__PURE__ */ new Map();
  emph = /* @__PURE__ */ new Map();
  hoveredOid = null;
  pinnedOids = /* @__PURE__ */ new Set();
  hoverPopupOid = null;
  loaded = false;
  pendingOrders = null;
  dirty = false;
  unbinders = [];
  onLoad;
  popupMount = new PopupMount();
  capacityUnit;
  onRender = null;
  onHover = null;
  /** Fires whenever the pinned set changes (pin/unpin, or a data update that drops stale pins),
   *  with the pinned order_ids in pin order. The single source of truth for pinned state. */
  onPinnedChange = null;
  emitPinned() {
    this.onPinnedChange?.([...this.pinnedOids]);
  }
  constructor(map, opts) {
    this.map = map;
    this.capacityUnit = opts.capacityUnit;
    this.popup = new maplibregl2.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 14,
      maxWidth: "340px",
      // pn-map-pop--hover sets pointer-events:none so the tooltip never blocks
      // map drag/zoom under the cursor.
      className: "pn-map-pop pn-map-pop--hover"
    });
    this.unbinders.push(registerOverlay(map, this));
    if (map.loaded()) this.init();
    else {
      this.onLoad = () => this.init();
      map.once("load", this.onLoad);
    }
  }
  init() {
    this.addLayers();
    this.wireEvents();
    const onStyle = () => this.reinstall();
    this.map.on("styledata", onStyle);
    this.unbinders.push(() => this.map.off("styledata", onStyle));
    this.loaded = true;
    if (this.pendingOrders) {
      const o = this.pendingOrders;
      this.pendingOrders = null;
      this.ordersData = o;
      this.orderVisible = o.map(() => true);
      this.applyData(true);
    }
  }
  reinstall() {
    if (!this.loaded || this.map.getSource(SRC2)) return;
    this.addLayers();
    if (this.ordersData.length) this.applyData(false);
  }
  addLayers() {
    this.map.addSource(SRC2, {
      type: "geojson",
      data: emptyFC2(),
      cluster: true,
      clusterRadius: 48,
      clusterMaxZoom: 14,
      promoteId: "oid"
    });
    this.map.addLayer({
      id: L_CLUSTER,
      type: "circle",
      source: SRC2,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": cssVar3("--primary", "#2563eb"),
        "circle-stroke-color": cssVar3("--primary-foreground", "#ffffff"),
        "circle-stroke-width": 2.5,
        "circle-radius": ["step", ["get", "point_count"], 16, 10, 19, 100, 22]
      }
    });
    this.map.addLayer({
      id: L_CLUSTER_COUNT,
      type: "symbol",
      source: SRC2,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["JetBrains Mono Bold", "Noto Sans Bold"],
        "text-size": 12,
        "text-allow-overlap": true,
        "text-ignore-placement": true
      },
      paint: { "text-color": cssVar3("--primary-foreground", "#ffffff") }
    });
    this.map.addLayer({
      id: L_POINT,
      type: "circle",
      source: SRC2,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": ["get", "statusColor"],
        "circle-stroke-color": cssVar3("--background", "#ffffff"),
        "circle-stroke-width": 2.5,
        "circle-radius": [
          "case",
          ["boolean", ["feature-state", "hovered"], false],
          10,
          7
        ],
        "circle-opacity": ["case", ["boolean", ["feature-state", "dimmed"], false], 0.1, 1],
        "circle-stroke-opacity": ["case", ["boolean", ["feature-state", "dimmed"], false], 0.1, 1]
      }
    });
    this.map.setPaintProperty(L_POINT, "circle-opacity-transition", { duration: 100, delay: 0 });
    this.map.setPaintProperty(L_POINT, "circle-stroke-opacity-transition", { duration: 100, delay: 0 });
  }
  applyClusterColors() {
    if (!this.map.getLayer(L_CLUSTER)) return;
    this.map.setPaintProperty(L_CLUSTER, "circle-color", cssVar3("--primary", "#2563eb"));
    this.map.setPaintProperty(L_CLUSTER, "circle-stroke-color", cssVar3("--primary-foreground", "#ffffff"));
    this.map.setPaintProperty(L_CLUSTER_COUNT, "text-color", cssVar3("--primary-foreground", "#ffffff"));
    this.map.setPaintProperty(L_POINT, "circle-stroke-color", cssVar3("--background", "#ffffff"));
  }
  // ── Public API ────────────────────────────────────────────────────────────
  setOrders(orders) {
    this.clearTransient();
    const list = Array.isArray(orders) ? orders.slice() : [];
    if (!this.loaded) {
      this.pendingOrders = list;
      this.ordersData = list;
      this.orderVisible = list.map(() => true);
      this.reconcilePins();
      this.onRender?.(this.ordersData);
      return;
    }
    this.ordersData = list;
    this.orderVisible = list.map(() => true);
    this.applyData(true);
    this.reconcilePins();
    this.onRender?.(this.ordersData);
  }
  /** Drop pins whose order is no longer present after a data update — keeps a sidebar's pinned
   *  set from holding onto an order that vanished. Emits only if something changed. */
  reconcilePins() {
    if (this.pinnedOids.size === 0) return;
    const valid = new Set(this.ordersData.map((o) => o.order_id));
    let changed = false;
    for (const oid of this.pinnedOids) {
      if (!valid.has(oid)) {
        this.pinnedOids.delete(oid);
        changed = true;
      }
    }
    if (changed) {
      this.applyEmphasis();
      this.emitPinned();
    }
  }
  clear() {
    this.clearTransient();
    this.ordersData = [];
    this.orderVisible = [];
    this.pendingOrders = null;
    this.oidByIndex = [];
    this.indexByOid.clear();
    this.emph.clear();
    const hadPins = this.pinnedOids.size > 0;
    this.pinnedOids.clear();
    if (this.loaded) this.getSource()?.setData(emptyFC2());
    if (hadPins) this.emitPinned();
    this.onRender?.(this.ordersData);
  }
  destroy() {
    if (this.onLoad) {
      this.map.off("load", this.onLoad);
      this.onLoad = void 0;
    }
    for (const off of this.unbinders) off();
    this.unbinders = [];
    this.popupMount.destroy();
    this.popup.remove();
    for (const id of [L_CLUSTER_COUNT, L_CLUSTER, L_POINT]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    if (this.map.getSource(SRC2)) this.map.removeSource(SRC2);
    this.loaded = false;
  }
  /** Status color for order `i` (semantic-token-derived), matching its sidebar dot. */
  orderColor(i) {
    return statusToken(this.ordersData[i]?.status);
  }
  /** Re-render with the active theme/palette status colors + cluster accent. */
  refreshColors() {
    if (!this.loaded) return;
    this.applyClusterColors();
    this.applyData(false);
  }
  isVisible(i) {
    return !!this.orderVisible[i];
  }
  isHovered(i) {
    return this.oidByIndex[i] != null && this.oidByIndex[i] === this.hoveredOid;
  }
  setVisible(i, visible) {
    if (i < 0 || i >= this.orderVisible.length) return;
    if (this.orderVisible[i] === visible) return;
    this.orderVisible[i] = visible;
    this.scheduleRebuild();
  }
  /** Zoom to a single order (in far enough that it un-clusters). */
  focusOrder(i) {
    const o = this.ordersData[i];
    if (!o) return;
    const { lat, lng } = o.location;
    if (lat == null || lng == null) return;
    this.map.easeTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 14), duration: 500 });
  }
  hoverOrder(i) {
    this.setHovered(this.oidByIndex[i] ?? null);
  }
  clearHover() {
    this.setHovered(null);
  }
  isPinned(i) {
    const oid = this.oidByIndex[i];
    return oid != null && this.pinnedOids.has(oid);
  }
  pin(i) {
    const oid = this.oidByIndex[i];
    if (oid == null || this.pinnedOids.has(oid)) return;
    this.pinnedOids.add(oid);
    this.applyEmphasis();
    this.emitPinned();
  }
  unpin(i) {
    const oid = this.oidByIndex[i];
    if (oid == null || !this.pinnedOids.has(oid)) return;
    this.pinnedOids.delete(oid);
    this.applyEmphasis();
    this.emitPinned();
  }
  togglePin(i) {
    if (this.isPinned(i)) this.unpin(i);
    else this.pin(i);
  }
  /** Extent of the currently-visible orders, or null when none are visible. */
  getBounds() {
    const b = new maplibregl2.LngLatBounds();
    let any = false;
    this.ordersData.forEach((o, i) => {
      if (!this.orderVisible[i]) return;
      const { lat, lng } = o.location;
      if (lat == null || lng == null) return;
      b.extend([lng, lat]);
      any = true;
    });
    return any ? b : null;
  }
  // ── Rendering ───────────────────────────────────────────────────────────────
  getSource() {
    return this.map.getSource(SRC2);
  }
  scheduleRebuild() {
    if (this.dirty || !this.loaded) return;
    this.dirty = true;
    queueMicrotask(() => {
      this.dirty = false;
      this.applyData(false);
    });
  }
  applyData(fit) {
    const src = this.getSource();
    if (!src) return;
    this.oidByIndex = this.ordersData.map((o, i) => o.order_id || o.order_ref || `o${i}`);
    this.indexByOid.clear();
    this.oidByIndex.forEach((oid, i) => this.indexByOid.set(oid, i));
    const features = [];
    const b = new maplibregl2.LngLatBounds();
    let any = false;
    this.ordersData.forEach((o, i) => {
      if (!this.orderVisible[i]) return;
      const { lat, lng } = o.location;
      if (lat == null || lng == null) return;
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lng, lat] },
        properties: { oid: this.oidByIndex[i], statusColor: this.orderColor(i), idx: i }
      });
      b.extend([lng, lat]);
      any = true;
    });
    src.setData({ type: "FeatureCollection", features });
    this.emph.clear();
    this.applyEmphasis();
    if (fit && any) this.map.fitBounds(b, { padding: 60, duration: 0 });
  }
  // ── Events ────────────────────────────────────────────────────────────────
  wireEvents() {
    const onMove = (e) => {
      const f = e.features?.[0];
      const i = f?.properties?.idx;
      if (!f || i == null) return;
      this.map.getCanvas().style.cursor = "pointer";
      const oid = this.oidByIndex[i] ?? null;
      this.setHovered(oid);
      const o = this.ordersData[i];
      if (!o) return;
      const [lng, lat] = f.geometry.coordinates;
      this.popup.setLngLat([lng, lat]);
      if (oid !== this.hoverPopupOid) {
        this.popup.setDOMContent(
          this.popupMount.render(
            React4.createElement(OrderPopup, {
              order: o,
              color: this.orderColor(i),
              variant: "hover",
              capacityUnit: this.capacityUnit
            })
          )
        );
        this.hoverPopupOid = oid;
      }
      this.popup.addTo(this.map);
    };
    const onLeave = () => {
      this.map.getCanvas().style.cursor = "";
      this.setHovered(null);
      this.popup.remove();
      this.hoverPopupOid = null;
    };
    const onClusterClick = (e) => {
      const f = e.features?.[0];
      const cid = f?.properties?.cluster_id;
      if (cid == null) return;
      const coords = f.geometry.coordinates;
      this.getSource()?.getClusterExpansionZoom(cid).then((zoom) => this.map.easeTo({ center: coords, zoom, duration: 400 })).catch(() => {
      });
    };
    const onClusterEnter = () => {
      this.map.getCanvas().style.cursor = "pointer";
    };
    const onClusterLeave = () => {
      this.map.getCanvas().style.cursor = "";
    };
    this.map.on("mousemove", L_POINT, onMove);
    this.map.on("mouseleave", L_POINT, onLeave);
    this.map.on("click", L_CLUSTER, onClusterClick);
    this.map.on("mouseenter", L_CLUSTER, onClusterEnter);
    this.map.on("mouseleave", L_CLUSTER, onClusterLeave);
    this.unbinders.push(
      () => this.map.off("mousemove", L_POINT, onMove),
      () => this.map.off("mouseleave", L_POINT, onLeave),
      () => this.map.off("click", L_CLUSTER, onClusterClick),
      () => this.map.off("mouseenter", L_CLUSTER, onClusterEnter),
      () => this.map.off("mouseleave", L_CLUSTER, onClusterLeave)
    );
  }
  // ── Hover / pin ────────────────────────────────────────────────────────────
  setHovered(oid) {
    if (this.hoveredOid === oid) return;
    this.hoveredOid = oid;
    this.applyEmphasis();
    this.onHover?.(oid != null ? this.indexByOid.get(oid) ?? null : null);
  }
  applyEmphasis() {
    const hov = this.hoveredOid;
    const emphasized = (oid) => oid === hov || this.pinnedOids.has(oid);
    for (const oid of this.oidByIndex) {
      this.safeSetState(oid, {
        hovered: emphasized(oid),
        dimmed: (hov !== null || this.pinnedOids.size > 0) && !emphasized(oid)
      });
    }
  }
  safeSetState(oid, state) {
    const sig = `${state.hovered ? 1 : 0}${state.dimmed ? 1 : 0}`;
    if (this.emph.get(oid) === sig) return;
    try {
      this.map.setFeatureState({ source: SRC2, id: oid }, state);
      this.emph.set(oid, sig);
    } catch {
    }
  }
  clearTransient() {
    this.hoveredOid = null;
    this.hoverPopupOid = null;
    this.popup.remove();
  }
};

// src/map/logistics/boundary-overlay.ts
import maplibregl3 from "maplibre-gl";
var SRC3 = "pn-boundaries";
var L_FILL = "pn-boundaries-fill";
var L_LINE = "pn-boundaries-outline";
var L_LABEL2 = "pn-boundaries-label";
function emptyFC3() {
  return { type: "FeatureCollection", features: [] };
}
function cssVar4(name, fallback) {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v ? toMapColor(v) : fallback;
}
function escHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function numOf(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : void 0;
}
var BoundaryOverlay = class {
  map;
  popup;
  mode = "geojson";
  data = [];
  byId = /* @__PURE__ */ new Map();
  vectorCfg = null;
  hoveredId = null;
  visible = false;
  dimmed = false;
  loaded = false;
  unbinders = [];
  onLoad;
  constructor(map) {
    this.map = map;
    this.popup = new maplibregl3.Popup({ closeButton: false, closeOnClick: false, offset: 8, maxWidth: "260px" });
    this.unbinders.push(registerOverlay(map, this));
    if (map.loaded()) this.init();
    else {
      this.onLoad = () => this.init();
      map.once("load", this.onLoad);
    }
  }
  init() {
    this.addLayers();
    this.wireEvents();
    this.applyVisibility();
    this.pushData();
    const onStyle = () => this.reinstall();
    this.map.on("styledata", onStyle);
    this.unbinders.push(() => this.map.off("styledata", onStyle));
    this.loaded = true;
  }
  /** After a base-style reload our layers/sources are gone; re-add + re-push. */
  reinstall() {
    if (!this.loaded || this.map.getSource(SRC3)) return;
    this.addLayers();
    this.applyVisibility();
    this.pushData();
  }
  /** Remove + re-add for the current mode — used when the source TYPE changes (MapLibre can't
   *  mutate a source's type/url in place). */
  rebuild() {
    for (const id of [L_LABEL2, L_LINE, L_FILL]) if (this.map.getLayer(id)) this.map.removeLayer(id);
    if (this.map.getSource(SRC3)) this.map.removeSource(SRC3);
    this.addLayers();
    this.applyVisibility();
    this.pushData();
  }
  pushData() {
    if (this.mode !== "geojson") return;
    const features = this.data.map((b) => ({
      type: "Feature",
      geometry: b.geometry,
      properties: { bid: b.id, name: b.name, kind: b.kind, areaKm2: b.areaKm2, population: b.population }
    }));
    this.map.getSource(SRC3)?.setData({ type: "FeatureCollection", features });
  }
  addLayers() {
    const vector = this.mode === "vector" && this.vectorCfg;
    if (vector) {
      this.map.addSource(SRC3, {
        type: "vector",
        url: this.vectorCfg.url,
        promoteId: { [this.vectorCfg.sourceLayer]: "bid" }
      });
    } else {
      this.map.addSource(SRC3, { type: "geojson", data: emptyFC3(), promoteId: "bid" });
    }
    const srcLayer = vector ? { "source-layer": this.vectorCfg.sourceLayer } : {};
    const before = this.firstSymbolLayerId();
    this.map.addLayer(
      {
        id: L_FILL,
        type: "fill",
        source: SRC3,
        ...srcLayer,
        paint: {
          "fill-color": cssVar4("--primary", "#2563eb"),
          "fill-opacity": ["case", ["boolean", ["feature-state", "hovered"], false], 0.16, 0.05]
        }
      },
      before
    );
    this.map.addLayer(
      {
        id: L_LINE,
        type: "line",
        source: SRC3,
        ...srcLayer,
        layout: { "line-join": "round" },
        paint: {
          "line-color": [
            "case",
            ["boolean", ["feature-state", "hovered"], false],
            cssVar4("--primary", "#2563eb"),
            cssVar4("--border", "#cbd5e1")
          ],
          "line-width": ["case", ["boolean", ["feature-state", "hovered"], false], 2, 1]
        }
      },
      before
    );
    this.map.addLayer({
      id: L_LABEL2,
      type: "symbol",
      source: SRC3,
      ...srcLayer,
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["JetBrains Mono Regular", "Noto Sans Regular"],
        "text-size": 12,
        "text-padding": 4,
        "symbol-placement": "point"
      },
      paint: {
        "text-color": cssVar4("--muted-foreground", "#475569"),
        "text-halo-color": cssVar4("--background", "#ffffff"),
        "text-halo-width": 1.4
      }
    });
    if (this.dimmed) this.applyDim();
  }
  firstSymbolLayerId() {
    for (const l of this.map.getStyle().layers ?? []) {
      if (l.type === "symbol") return l.id;
    }
    return void 0;
  }
  // ── Public API ────────────────────────────────────────────────────────────
  /** Set the region polygons from in-memory GeoJSON. */
  setBoundaries(boundaries) {
    const list = Array.isArray(boundaries) ? boundaries.slice() : [];
    const wasVector = this.mode === "vector";
    this.mode = "geojson";
    this.vectorCfg = null;
    this.data = list;
    this.byId.clear();
    for (const b of list) this.byId.set(b.id, b);
    if (!this.loaded) return;
    if (wasVector) this.rebuild();
    else this.pushData();
  }
  /** Point the layer at a vector-tile source (e.g. a pmtiles:// archive). Only the viewport's
   *  tiles load per zoom. Tile features must carry the Boundary fields as properties. */
  setVectorSource(cfg) {
    this.mode = "vector";
    this.vectorCfg = cfg;
    this.data = [];
    this.byId.clear();
    this.hoveredId = null;
    if (!this.loaded) return;
    this.rebuild();
  }
  clear() {
    this.setHovered(null);
    this.popup.remove();
    this.data = [];
    this.byId.clear();
    if (this.loaded && this.mode === "geojson") {
      this.map.getSource(SRC3)?.setData(emptyFC3());
    }
  }
  /** Whole-layer show/hide of the region fill/outline/labels. */
  setVisible(visible) {
    if (this.visible === visible) return;
    this.visible = visible;
    if (!visible) {
      this.setHovered(null);
      this.popup.remove();
    }
    this.applyVisibility();
  }
  isVisible() {
    return this.visible;
  }
  /** Fade the boundary layers without hiding them — e.g. while a route/marker is focused. */
  setDimmed(dim) {
    this.dimmed = dim;
    this.applyDim();
  }
  applyDim() {
    if (!this.map.getLayer(L_FILL)) return;
    const dim = this.dimmed;
    this.map.setPaintProperty(
      L_FILL,
      "fill-opacity",
      dim ? 0.02 : ["case", ["boolean", ["feature-state", "hovered"], false], 0.16, 0.05]
    );
    this.map.setPaintProperty(L_LINE, "line-opacity", dim ? 0.2 : 1);
    this.map.setPaintProperty(L_LABEL2, "text-opacity", dim ? 0.2 : 1);
  }
  /** Extent of the boundary layer, or null when toggled off. Vector mode uses the configured
   *  camera bounds; geojson mode unions the polygon coords. */
  getBounds() {
    if (!this.visible) return null;
    if (this.mode === "vector") {
      const b = this.vectorCfg?.bounds;
      return b ? new maplibregl3.LngLatBounds([b[0], b[1]], [b[2], b[3]]) : null;
    }
    const bounds = new maplibregl3.LngLatBounds();
    let any = false;
    for (const bd of this.data) {
      for (const [lng, lat] of this.coords(bd.geometry)) {
        bounds.extend([lng, lat]);
        any = true;
      }
    }
    return any ? bounds : null;
  }
  /** Re-resolve token colors after a theme/palette swap. */
  refreshColors() {
    if (!this.loaded) return;
    const primary = cssVar4("--primary", "#2563eb");
    const border = cssVar4("--border", "#cbd5e1");
    this.map.setPaintProperty(L_FILL, "fill-color", primary);
    this.map.setPaintProperty(L_LINE, "line-color", [
      "case",
      ["boolean", ["feature-state", "hovered"], false],
      primary,
      border
    ]);
    this.map.setPaintProperty(L_LABEL2, "text-color", cssVar4("--muted-foreground", "#475569"));
    this.map.setPaintProperty(L_LABEL2, "text-halo-color", cssVar4("--background", "#ffffff"));
  }
  /** Detach listeners + remove layers + source. Idempotent. */
  destroy() {
    if (this.onLoad) {
      this.map.off("load", this.onLoad);
      this.onLoad = void 0;
    }
    for (const off of this.unbinders) off();
    this.unbinders = [];
    this.popup.remove();
    for (const id of [L_LABEL2, L_LINE, L_FILL]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    if (this.map.getSource(SRC3)) this.map.removeSource(SRC3);
    this.loaded = false;
  }
  // ── Internals ───────────────────────────────────────────────────────────────
  applyVisibility() {
    const v = this.visible ? "visible" : "none";
    for (const id of [L_FILL, L_LINE, L_LABEL2]) {
      if (this.map.getLayer(id)) this.map.setLayoutProperty(id, "visibility", v);
    }
  }
  wireEvents() {
    const onMove = (e) => {
      if (!this.visible) return;
      const f = e.features?.[0];
      if (!f || f.id == null) return;
      this.map.getCanvas().style.cursor = "pointer";
      this.setHovered(String(f.id));
      const bd = this.mode === "vector" ? this.boundaryFromFeature(f) : this.byId.get(String(f.id));
      if (bd) this.popup.setLngLat(e.lngLat).setHTML(this.popupHtml(bd)).addTo(this.map);
    };
    const onLeave = () => {
      this.map.getCanvas().style.cursor = "";
      this.setHovered(null);
      this.popup.remove();
    };
    this.map.on("mousemove", L_FILL, onMove);
    this.map.on("mouseleave", L_FILL, onLeave);
    this.unbinders.push(
      () => this.map.off("mousemove", L_FILL, onMove),
      () => this.map.off("mouseleave", L_FILL, onLeave)
    );
  }
  boundaryFromFeature(f) {
    const p = f.properties || {};
    const name = typeof p.name === "string" ? p.name : null;
    if (!name) return null;
    return {
      id: String(f.id),
      name,
      geometry: { type: "Polygon", coordinates: [] },
      kind: typeof p.kind === "string" ? p.kind : void 0,
      areaKm2: numOf(p.areaKm2),
      population: numOf(p.population)
    };
  }
  setHovered(id) {
    if (this.hoveredId === id) return;
    if (this.hoveredId != null) this.safeSetState(this.hoveredId, { hovered: false });
    this.hoveredId = id;
    if (id != null) this.safeSetState(id, { hovered: true });
  }
  safeSetState(id, state) {
    try {
      const ref = this.mode === "vector" && this.vectorCfg ? { source: SRC3, sourceLayer: this.vectorCfg.sourceLayer, id } : { source: SRC3, id };
      this.map.setFeatureState(ref, state);
    } catch {
    }
  }
  popupHtml(b) {
    const rows = [];
    if (b.kind) rows.push(`<div class="pn-boundary-pop-sub">${escHtml(b.kind)}</div>`);
    const stats = [];
    if (b.areaKm2 != null) stats.push(`${b.areaKm2.toLocaleString()} km\xB2`);
    if (b.population != null) stats.push(`${b.population.toLocaleString()} pop.`);
    if (stats.length) rows.push(`<div class="pn-boundary-pop-stats">${escHtml(stats.join(" \xB7 "))}</div>`);
    return `<div class="pn-boundary-pop"><div class="pn-boundary-pop-name">${escHtml(b.name)}</div>${rows.join("")}</div>`;
  }
  coords(geom) {
    const out = [];
    const rings = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
    for (const poly of rings) {
      const outer = poly[0] || [];
      for (const c of outer) out.push([c[0], c[1]]);
    }
    return out;
  }
};

// src/map/logistics/overlays.ts
function createLogisticsOverlays(map, capacityUnit) {
  return {
    routes: new RouteOverlay(map, { capacityUnit }),
    orders: new OrderOverlay(map, { capacityUnit }),
    boundaries: new BoundaryOverlay(map)
  };
}

// src/map/logistics/logistics-map.tsx
import * as React6 from "react";

// src/map/logistics/pinned-deck.tsx
import * as React5 from "react";
import { AnimatePresence, LazyMotion, domMax, m, useReducedMotion } from "motion/react";
import { jsx as jsx3 } from "react/jsx-runtime";
var HOLD_MS = 400;
var CardBody = React5.memo(function CardBody2({
  route,
  color,
  capacityUnit,
  stopsOpen,
  index,
  onToggle,
  onClose
}) {
  return /* @__PURE__ */ jsx3(
    RoutePopup,
    {
      route,
      color,
      variant: "detail",
      capacityUnit,
      maxHeight: "none",
      stopsOpen,
      onStopsOpenChange: (open) => onToggle(route.path_id, open),
      onClose: () => onClose(index)
    }
  );
});
var PinnedDeck = React5.memo(function PinnedDeck2({ pins, capacityUnit, onUnpin, onCardHover }) {
  const [expanded, setExpanded] = React5.useState({});
  const reduce = useReducedMotion();
  React5.useEffect(() => {
    const valid = new Set(pins.map((p) => p.route.path_id));
    setExpanded((e) => {
      const keys = Object.keys(e);
      if (keys.every((k) => valid.has(k))) return e;
      const next = {};
      for (const k of keys) if (valid.has(k)) next[k] = e[k];
      return next;
    });
  }, [pins]);
  const handleToggle = React5.useCallback(
    (id, open) => setExpanded((e) => ({ ...e, [id]: open })),
    []
  );
  const listRef = React5.useRef(null);
  const holdTimer = React5.useRef(null);
  React5.useEffect(() => () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
  }, []);
  const handleClose = React5.useCallback(
    (index) => {
      const list = listRef.current;
      if (list) {
        list.style.minHeight = `${list.offsetHeight}px`;
        if (holdTimer.current) clearTimeout(holdTimer.current);
        holdTimer.current = setTimeout(() => {
          if (listRef.current) listRef.current.style.minHeight = "";
        }, HOLD_MS);
      }
      onUnpin(index);
    },
    [onUnpin]
  );
  const transition = reduce ? { duration: 0 } : {
    layout: { type: "spring", stiffness: 350, damping: 30 },
    opacity: { duration: 0.2 },
    scale: { duration: 0.2 }
  };
  return /* @__PURE__ */ jsx3(LazyMotion, { features: domMax, children: /* @__PURE__ */ jsx3(ScrollArea, { className: "pointer-events-auto max-h-full w-66 self-start", children: /* @__PURE__ */ jsx3("div", { ref: listRef, className: "relative flex flex-col gap-2", children: /* @__PURE__ */ jsx3(AnimatePresence, { mode: "popLayout", initial: false, children: pins.map(({ route, color, index }) => /* @__PURE__ */ jsx3(
    m.div,
    {
      layout: "position",
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.8, x: -100 },
      transition,
      className: "w-66 overflow-hidden rounded-md border border-border bg-popover shadow-md",
      onMouseEnter: () => onCardHover?.(index),
      onMouseLeave: () => onCardHover?.(null),
      children: /* @__PURE__ */ jsx3(
        CardBody,
        {
          route,
          color,
          capacityUnit,
          stopsOpen: expanded[route.path_id] ?? false,
          index,
          onToggle: handleToggle,
          onClose: handleClose
        }
      )
    },
    route.path_id
  )) }) }) }) });
});

// src/map/logistics/logistics-map.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var isDarkNow = () => typeof document !== "undefined" && document.documentElement.classList.contains("dark");
var DECK_RESERVED_PX = 300;
var DECK_MIN_MAP_PX = 336;
var LogisticsMap = React6.forwardRef(function LogisticsMap2({
  routes,
  orders,
  boundaries,
  mode,
  capacityUnit,
  onHover,
  onPinnedChange,
  onOrderPinnedChange,
  className,
  style,
  ...mapProps
}, ref) {
  const handleRef = React6.useRef(null);
  const overlaysRef = React6.useRef(null);
  const wrapRef = React6.useRef(null);
  const [ready, setReady] = React6.useState(false);
  const [dark, setDark] = React6.useState(isDarkNow);
  const [pinnedPathIds, setPinnedPathIds] = React6.useState([]);
  const onHoverRef = React6.useRef(onHover);
  onHoverRef.current = onHover;
  const onPinnedChangeRef = React6.useRef(onPinnedChange);
  onPinnedChangeRef.current = onPinnedChange;
  const onOrderPinnedChangeRef = React6.useRef(onOrderPinnedChange);
  onOrderPinnedChangeRef.current = onOrderPinnedChange;
  const pinnedRef = React6.useRef(pinnedPathIds);
  pinnedRef.current = pinnedPathIds;
  const recenterWithDeck = React6.useCallback((opts) => {
    const h = handleRef.current;
    if (!h) return false;
    const w = h.map?.getContainer().clientWidth ?? 0;
    const deckShown = pinnedRef.current.length > 0 && w >= DECK_MIN_MAP_PX;
    if (!deckShown) return h.recenter(opts);
    const base = opts?.padding ?? 60;
    const pad = typeof base === "number" ? { top: base, bottom: base, left: base, right: base } : { top: 0, bottom: 0, left: 0, right: 0, ...base };
    return h.recenter({ duration: 500, ...opts, padding: { ...pad, right: pad.right + DECK_RESERVED_PX } });
  }, []);
  const capacityUnitRef = React6.useRef(capacityUnit);
  capacityUnitRef.current = capacityUnit;
  React6.useEffect(() => () => {
    overlaysRef.current?.routes.destroy();
    overlaysRef.current?.orders.destroy();
    overlaysRef.current?.boundaries.destroy();
    overlaysRef.current = null;
  }, []);
  const handleReady = React6.useCallback((h) => {
    overlaysRef.current?.routes.destroy();
    overlaysRef.current?.orders.destroy();
    overlaysRef.current?.boundaries.destroy();
    handleRef.current = h;
    h.setCaptureRoot(wrapRef.current);
    const ov = createLogisticsOverlays(h.map, capacityUnitRef.current);
    overlaysRef.current = ov;
    ov.routes.onHover = (i) => onHoverRef.current?.(i);
    ov.orders.onHover = (i) => onHoverRef.current?.(i);
    ov.routes.onPinnedChange = (ids) => {
      setPinnedPathIds([...ids]);
      onPinnedChangeRef.current?.(ids);
    };
    ov.orders.onPinnedChange = (ids) => onOrderPinnedChangeRef.current?.(ids);
    setReady(true);
  }, []);
  React6.useImperativeHandle(
    ref,
    () => ({
      get map() {
        return handleRef.current?.map ?? null;
      },
      get routes() {
        return overlaysRef.current?.routes ?? null;
      },
      get orders() {
        return overlaysRef.current?.orders ?? null;
      },
      get boundaries() {
        return overlaysRef.current?.boundaries ?? null;
      },
      recenter: (opts) => recenterWithDeck(opts),
      exportPng: (kind) => {
        void handleRef.current?.exportPng(kind);
      },
      copyToClipboard: (kind) => {
        void handleRef.current?.copyToClipboard(kind);
      }
    }),
    []
  );
  const handleReframe = React6.useCallback(() => {
    recenterWithDeck();
  }, [recenterWithDeck]);
  const showRoutes = mode !== "orders";
  const showOrders = mode !== "routes";
  React6.useEffect(() => {
    if (ready) overlaysRef.current?.routes.setRoutes(showRoutes ? routes ?? [] : []);
  }, [ready, routes, showRoutes]);
  React6.useEffect(() => {
    if (ready) overlaysRef.current?.orders.setOrders(showOrders ? orders ?? [] : []);
  }, [ready, orders, showOrders]);
  React6.useEffect(() => {
    if (!ready) return;
    const b = overlaysRef.current?.boundaries;
    if (!b) return;
    b.setBoundaries(boundaries ?? []);
    b.setVisible(!!boundaries?.length);
  }, [ready, boundaries]);
  React6.useEffect(() => {
    const html = document.documentElement;
    const sync = () => {
      setDark(html.classList.contains("dark"));
      requestAnimationFrame(() => {
        const ov = overlaysRef.current;
        if (!ov) return;
        ov.routes.refreshColors();
        ov.orders.refreshColors();
        ov.boundaries.refreshColors();
      });
    };
    const obs = new MutationObserver(sync);
    obs.observe(html, { attributes: true, attributeFilter: ["class", "data-semantics"] });
    return () => obs.disconnect();
  }, []);
  const pinsMounted = React6.useRef(false);
  React6.useEffect(() => {
    if (!ready) return;
    if (!pinsMounted.current) {
      pinsMounted.current = true;
      return;
    }
    recenterWithDeck({ padding: 60, duration: 400 });
  }, [ready, pinnedPathIds, recenterWithDeck]);
  const pins = React6.useMemo(() => {
    const list = routes ?? [];
    const overlay = overlaysRef.current?.routes;
    const out = [];
    for (const id of pinnedPathIds) {
      const i = list.findIndex((r) => r.path_id === id);
      if (i < 0) continue;
      out.push({ route: list[i], color: overlay?.routeColor(i) ?? "", index: i });
    }
    return out;
  }, [pinnedPathIds, routes]);
  const handleUnpin = React6.useCallback((i) => overlaysRef.current?.routes.unpin(i), []);
  const handleCardHover = React6.useCallback((i) => {
    const o = overlaysRef.current?.routes;
    if (i == null) o?.clearHover();
    else o?.hoverRoute(i);
  }, []);
  return /* @__PURE__ */ jsxs3("div", { ref: wrapRef, className: cn("pn-map-wrap relative @container", className), style, children: [
    /* @__PURE__ */ jsx4(Map2, { ...mapProps, dark, onReady: handleReady, onReframe: handleReframe }),
    /* @__PURE__ */ jsx4("div", { className: "pointer-events-none absolute inset-0 z-10 flex justify-end p-2.5 @max-[21rem]:hidden", children: /* @__PURE__ */ jsx4(PinnedDeck, { pins, capacityUnit, onUnpin: handleUnpin, onCardHover: handleCardHover }) })
  ] });
});

// src/map/logistics/route-list-item.tsx
import "react";
import "lucide-react";

// src/map/logistics/list-item-toggles.tsx
import "react";
import { Pin, Eye, EyeOff } from "lucide-react";
import { jsx as jsx5 } from "react/jsx-runtime";
var TOGGLE_SIZE = "size-6 min-w-0 rounded-md p-0 [&_svg:not([class*='size-'])]:size-3";
function PinToggle({ pinned, onPinnedChange, className }) {
  return /* @__PURE__ */ jsx5(
    Toggle,
    {
      pressed: pinned,
      onPressedChange: onPinnedChange,
      "aria-label": pinned ? "Unpin" : "Pin",
      className: cn(
        TOGGLE_SIZE,
        "text-muted-foreground",
        "data-[state=on]:bg-primary/15 data-[state=on]:text-primary data-[state=on]:[&_svg]:fill-current",
        className
      ),
      children: /* @__PURE__ */ jsx5(Pin, {})
    }
  );
}
function VisibilityToggle({ visible, onVisibleChange, className }) {
  return /* @__PURE__ */ jsx5(
    Toggle,
    {
      pressed: !visible,
      onPressedChange: (hidden) => onVisibleChange(!hidden),
      "aria-label": visible ? "Hide" : "Show",
      className: cn(TOGGLE_SIZE, "data-[state=on]:bg-muted data-[state=on]:text-muted-foreground", className),
      children: visible ? /* @__PURE__ */ jsx5(Eye, {}) : /* @__PURE__ */ jsx5(EyeOff, {})
    }
  );
}

// src/map/logistics/route-list-item.tsx
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function RouteListItem({
  route,
  color,
  visible,
  pinned,
  onHoverChange,
  onVisibleChange,
  onPinnedChange,
  capacityUnit,
  icon: Icon,
  format,
  className
}) {
  const rows = routeSummaryRows(route, capacityUnit, format);
  const status = statusToken(route.status);
  return /* @__PURE__ */ jsxs4(
    Item,
    {
      variant: "outline",
      size: "sm",
      className: cn("relative flex-col items-stretch gap-2 text-2xs", !visible && "opacity-50", className),
      onMouseEnter: () => onHoverChange(true),
      onMouseLeave: () => onHoverChange(false),
      children: [
        /* @__PURE__ */ jsx6(
          "span",
          {
            "aria-hidden": "true",
            className: "absolute inset-y-1.5 left-1.5 w-1 rounded-full",
            style: { backgroundColor: color }
          }
        ),
        /* @__PURE__ */ jsxs4(ItemTitle, { className: "w-full items-center gap-2 text-2xs", children: [
          Icon ? /* @__PURE__ */ jsx6(ItemMedia, { variant: "icon", className: "translate-y-0 self-center", style: { color, borderColor: color }, children: /* @__PURE__ */ jsx6(Icon, {}) }) : null,
          /* @__PURE__ */ jsx6("span", { className: "min-w-0 flex-1 truncate font-mono font-bold", style: { color }, children: route.ship_ref || route.label || route.path_id }),
          /* @__PURE__ */ jsx6(Badge, { variant: "outline", className: "shrink-0 font-mono uppercase text-2xs", style: { color: status, borderColor: status }, children: route.status || "\u2014" }),
          /* @__PURE__ */ jsxs4(ItemActions, { className: "shrink-0", children: [
            /* @__PURE__ */ jsx6(PinToggle, { pinned, onPinnedChange }),
            /* @__PURE__ */ jsx6(VisibilityToggle, { visible, onVisibleChange })
          ] })
        ] }),
        /* @__PURE__ */ jsx6(MetricRows, { rows, className: "text-2xs" })
      ]
    }
  );
}

// src/map/logistics/order-list-item.tsx
import "react";
import "lucide-react";
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function OrderListItem({
  order,
  color,
  visible,
  pinned,
  onHoverChange,
  onVisibleChange,
  onPinnedChange,
  capacityUnit,
  icon: Icon,
  format,
  className
}) {
  const loc = order.location;
  const locLabel = [loc.name, loc.code ? `(${loc.code})` : null].filter(Boolean).join(" ");
  const rows = [
    ...locLabel ? [{ label: "Location", value: locLabel }] : [],
    ...orderSummaryRows(order, capacityUnit, format)
  ];
  const status = statusToken(order.status);
  return /* @__PURE__ */ jsxs5(
    Item,
    {
      variant: "outline",
      size: "sm",
      className: cn("relative flex-col items-stretch gap-2 text-2xs", !visible && "opacity-50", className),
      onMouseEnter: () => onHoverChange(true),
      onMouseLeave: () => onHoverChange(false),
      children: [
        /* @__PURE__ */ jsx7(
          "span",
          {
            "aria-hidden": "true",
            className: "absolute inset-y-1.5 left-1.5 w-1 rounded-full",
            style: { backgroundColor: color }
          }
        ),
        /* @__PURE__ */ jsxs5(ItemTitle, { className: "w-full items-center gap-2 text-2xs", children: [
          Icon ? /* @__PURE__ */ jsx7(ItemMedia, { variant: "icon", className: "translate-y-0 self-center", style: { color, borderColor: color }, children: /* @__PURE__ */ jsx7(Icon, {}) }) : null,
          /* @__PURE__ */ jsx7("span", { className: "min-w-0 flex-1 truncate font-mono font-bold", style: { color }, children: order.order_ref || order.order_id }),
          /* @__PURE__ */ jsx7(Badge, { variant: "outline", className: "shrink-0 font-mono uppercase text-2xs", style: { color: status, borderColor: status }, children: order.status || "\u2014" }),
          /* @__PURE__ */ jsxs5(ItemActions, { className: "shrink-0", children: [
            /* @__PURE__ */ jsx7(PinToggle, { pinned, onPinnedChange }),
            /* @__PURE__ */ jsx7(VisibilityToggle, { visible, onVisibleChange })
          ] })
        ] }),
        /* @__PURE__ */ jsx7(MetricRows, { rows, className: "text-2xs" })
      ]
    }
  );
}

// src/map/logistics/sort-pinned.ts
function sortByPinned(items, pinnedIdsInPinOrder, idOf) {
  const pinnedSet = new Set(pinnedIdsInPinOrder);
  const byId = new Map(items.map((item) => [idOf(item), item]));
  const pinned = pinnedIdsInPinOrder.map((id) => byId.get(id)).filter((item) => item != null);
  const rest = items.filter((item) => !pinnedSet.has(idOf(item)));
  return [...pinned, ...rest];
}
export {
  BoundaryOverlay,
  LogisticsMap,
  MetricRows,
  OrderListItem,
  OrderOverlay,
  OrderPopup,
  PopupMount,
  RouteListItem,
  RouteOverlay,
  RoutePopup,
  createLogisticsOverlays,
  esc,
  fmtDateTime,
  fmtDateTimeNumeric,
  fmtHours,
  fmtKm,
  fmtMin,
  fmtPct,
  fmtTime,
  fmtVolume,
  haversineTotalM,
  orderSummaryRows,
  routeColor,
  routePalette,
  routeSummaryRows,
  sortByPinned,
  statusToken
};
//# sourceMappingURL=index.js.map